/**
 * Re-focus — the short ritual. What arrived since you last looked, what you're
 * committed to now, then close it out (which stamps the view so next time's
 * "what's new" is honest).
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, Eyebrow, PrimaryButton, StatusChip } from '../components/primitives';
import { StepRail, type FlowStep } from '../components/flow';
import { useFocusActions, useFocusList } from './useFocusList';

export default function RefocusScreen() {
  const app = getAppDefinition('focus')!;
  const navigate = useNavigate();
  const { collectionId } = useParams<{ collectionId: string }>();
  const { collection, committed, pool, entries } = useFocusList(collectionId ?? null);
  const actions = useFocusActions();
  const items = useItemsStore((s) => s.items);

  const [closing, setClosing] = useState(false);
  const [closed, setClosed] = useState(false);

  /** Items on the list that arrived since the last time this list was viewed. */
  const delta = useMemo(
    () => (collectionId ? actions.getDelta(collectionId, items) : { newCount: 0, newItemIds: [] }),
    [collectionId, actions, items],
  );
  const newOnes = useMemo(() => {
    const ids = new Set(delta.newItemIds);
    return entries.filter((e) => ids.has(e.item.id));
  }, [delta.newItemIds, entries]);

  const steps: FlowStep[] = [
    {
      key: 'new',
      label: "What's new",
      detail:
        delta.newCount > 0
          ? `${delta.newCount} arrived since you last looked`
          : 'Nothing new since last time',
      status: delta.newCount > 0 ? 'warn' : 'done',
    },
    {
      key: 'commit',
      label: 'What we pin',
      detail:
        committed.length > 0
          ? `${committed.length} pinned, ${pool.length} in the pool`
          : 'Nothing pinned yet',
      status: committed.length > 0 ? 'done' : 'warn',
    },
    {
      key: 'close',
      label: 'Close the ritual',
      detail: closed ? 'Done — next time starts from here' : 'Stamps what you just reviewed',
      status: closed ? 'done' : 'current',
    },
  ];

  const closeRitual = async () => {
    if (!collection) return;
    setClosing(true);
    await actions.closeRitual(
      collection.id,
      entries.map((e) => e.item.id),
    );
    setClosing(false);
    setClosed(true);
  };

  if (!collection) {
    return (
      <AppShell app={app} backTo="/apps/focus">
        <EmptyState title="Loading…" />
      </AppShell>
    );
  }

  return (
    <AppShell app={app} backTo={`/apps/focus/${collection.id}`} backLabel="Back to the list">
      <ScreenHeading
        title="Re-focus"
        subtitle="Ten minutes: see what arrived, confirm the few, and close it out."
      />
      <BallLine ballWith={closed ? 'the team — go do the work' : 'you — run the ritual'} />

      <div className="space-y-4">
        <StepRail steps={steps} />

        <AppCard>
          <Eyebrow>What&apos;s new</Eyebrow>
          {newOnes.length === 0 ? (
            <p className="mt-2 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
              Nothing arrived since your last re-focus. The list you pinned still stands.
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {newOnes.map((entry) => (
                <li key={entry.item.id} className="flex items-center gap-3">
                  <Sparkles className="h-4 w-4 shrink-0 text-electric-mustard-600" />
                  <span className="min-w-0 flex-1 truncate font-body text-sm text-gray-900 dark:text-[var(--text-pri)]">
                    {entry.item.title}
                  </span>
                  {entry.committed ? (
                    <StatusChip label="Pinned" tone="mustard" />
                  ) : (
                    <button
                      type="button"
                      onClick={() => void actions.commit(collection.id, entry.item.id)}
                      className="inline-flex items-center gap-1 rounded-md bg-primary-600 px-2.5 py-1 font-body text-xs font-medium text-white hover:bg-primary-700"
                    >
                      <Check className="h-3 w-3" /> Pin
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </AppCard>

        <AppCard>
          <Eyebrow>Pinned · {committed.length}</Eyebrow>
          {committed.length === 0 ? (
            <p className="mt-2 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
              Nothing is pinned. Go back to the list and pin the few that matter.
            </p>
          ) : (
            <ol className="mt-3 space-y-2">
              {committed.map((entry, i) => (
                <li key={entry.item.id} className="flex items-center gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-electric-mustard-100 font-mono text-xs text-electric-mustard-800 dark:bg-electric-mustard-900/40 dark:text-electric-mustard-300">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-body text-sm text-gray-900 dark:text-[var(--text-pri)]">
                    {entry.item.title}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </AppCard>

        <div className="flex items-center justify-end gap-3">
          {closed ? (
            <>
              <span className="font-body text-sm text-alpine-green-700 dark:text-alpine-green-300">
                Ritual closed.
              </span>
              <PrimaryButton onClick={() => navigate(`/apps/focus/${collection.id}`)}>
                Back to the list
              </PrimaryButton>
            </>
          ) : (
            <PrimaryButton onClick={() => void closeRitual()} disabled={closing}>
              {closing ? 'Closing…' : 'Close the ritual'}
            </PrimaryButton>
          )}
        </div>
      </div>
    </AppShell>
  );
}
